import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import { PiSpinnerGapLight } from 'react-icons/pi';

interface DeleteDataSourceConfirmationProps {
  count?: number;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const DeleteDataSourceConfirmation: React.FC<DeleteDataSourceConfirmationProps> = ({
  count = 1,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const isPlural = count > 1;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <PiSpinnerGapLight className="w-12 h-12 text-red-500 animate-spin" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isPlural
              ? t(`Deleting ${count} data sources...`)
              : t('Deleting data source...')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isPlural
            ? t('This will permanently delete the selected data sources, including all uploaded documents and search indexes. This action cannot be undone.')
            : t('This will permanently delete this data source, including all uploaded documents and the search index. This action cannot be undone.')}
        </p>
      </div>

      <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-gray-300 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button
          variant="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="w-24"
        >
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteDataSourceConfirmation;
